import {
    Box,
    TextField,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Typography,
    Collapse,
    InputAdornment,
} from "@mui/material";
import {
    Folder as FolderIcon,
    Description as FileIcon,
    ExpandLess,
    ExpandMore,
    Search as SearchIcon,
} from "@mui/icons-material";
import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../configureStore";
import { ConfigListItem } from "../../interface/configs";
import { useConfigEditorActions } from "../../hooks/useConfigEditorActions";

interface ConfigListProps {
    selectedConfigName: string | null;
}

// Helper to extract form type from config name (e.g., "1040-2021" -> "1040")
const getFormType = (name: string): string => {
    const displayName = name.replace('configs/', '').replace('.json', '');
    // Match pattern like "1040-2021", "schedule_k-1_1065-default"
    const match = displayName.match(/^(.+?)-(default|\d{4})$/);
    if (match) {
        return match[1];
    }
    return displayName;
};

// Helper to extract year/variant from config name
const getVariant = (name: string): string => {
    const displayName = name.replace('configs/', '').replace('.json', '');
    const match = displayName.match(/-(default|\d{4})$/);
    return match ? match[1] : '';
};

const ConfigList = ({
    selectedConfigName,
}: ConfigListProps) => {
    const { fetchList, fetchContent } = useConfigEditorActions();
    const configs = useSelector<RootState, ConfigListItem[]>(({ configs: c }) => c.configs);
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['configs/GET_CONFIGS']);

    const [searchQuery, setSearchQuery] = useState<string>("");
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['1040']));

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const filteredConfigs = useMemo(() => {
        if (!searchQuery) return configs;
        const query = searchQuery.toLowerCase();
        return configs.filter((c) => c.displayName.toLowerCase().includes(query)
            || c.name.toLowerCase().includes(query));
    }, [configs, searchQuery]);

    // Group configs by form type
    const groupedConfigs = useMemo(() => {
        const groups: { [formType: string]: ConfigListItem[] } = {};

        filteredConfigs.forEach((config) => {
            const formType = getFormType(config.name);
            if (!groups[formType]) {
                groups[formType] = [];
            }
            groups[formType].push(config);
        });

        // Sort each group by variant (default first, then years ascending)
        Object.keys(groups).forEach((key) => {
            groups[key].sort((a, b) => {
                const varA = getVariant(a.name);
                const varB = getVariant(b.name);
                if (varA === 'default') return -1;
                if (varB === 'default') return 1;
                return varA.localeCompare(varB);
            });
        });

        return groups;
    }, [filteredConfigs]);

    const handleFolderClick = (formType: string) => {
        setExpandedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(formType)) {
                next.delete(formType);
            } else {
                next.add(formType);
            }
            return next;
        });
    };

    const handleConfigClick = (config: ConfigListItem) => {
        // Extract config name without prefix and extension for API call
        const configName = config.name.replace('configs/', '').replace('.json', '');
        fetchContent({ input: { configName } });
    };

    if (loading && configs.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const formTypes = Object.keys(groupedConfigs).sort();

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2 }}>
                <TextField
                    placeholder="Search configs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <List dense>
                    {formTypes.map((formType) => (
                        <Box key={formType}>
                            <ListItemButton onClick={() => handleFolderClick(formType)}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <FolderIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={formType}
                                    secondary={`${groupedConfigs[formType].length} configs`}
                                />
                                {expandedFolders.has(formType) ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                            <Collapse in={expandedFolders.has(formType)} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding dense>
                                    {groupedConfigs[formType].map((config) => {
                                        const variant = getVariant(config.name);
                                        return (
                                            <ListItemButton
                                                key={config.name}
                                                sx={{ pl: 4 }}
                                                selected={selectedConfigName === config.name}
                                                onClick={() => handleConfigClick(config)}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <FileIcon fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={variant || config.displayName}
                                                    secondary={new Date(config.lastModified).toLocaleDateString()}
                                                    slotProps={{
                                                        primary: { component: 'div' },
                                                        secondary: { component: 'div' },
                                                    }}
                                                />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        </Box>
                    ))}

                    {filteredConfigs.length === 0 && (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                {searchQuery ? 'No configs found' : 'No configs available'}
                            </Typography>
                        </Box>
                    )}
                </List>
            </Box>
        </Box>
    );
};

export default ConfigList;
